import { CONNECTION_KIND_GLYPH, CONNECTION_KIND_LABELS, type ConnectionKind } from '../../../../types/network';
import { fmt } from '../../../../utils/numberFormat';
import { LOADING_WARNING_PCT } from '../../../../utils/loadingConstants';
/**
 * Shared marker icon HTML generators.
 * Pure functions that generate icon HTML - no store access, no side effects.
 * Used by both NetworkMarker (live) and ScenarioMarker (stress scenarios).
 */

export interface IconOptions {
  iconSize: [number, number];
  iconAnchor: [number, number];
}

export interface HausanschlussIconProps {
  isSelected: boolean;
  isHighlighted: boolean;
  hasSMGW: boolean;
  hasSteuerbox: boolean;
  loadPercentage?: number;
  isOverloaded?: boolean;
  isLimited?: boolean;
  technischeRessourcen?: Array<{ type: string }>;
  /** Anschlussart — picks the glyph inside the circle (default household ⌂). */
  connectionKind?: ConnectionKind;
  // PV stress test specific
  isExporting?: boolean;   // True if net power is negative (exporting to grid)
  hasPv?: boolean;         // True if has PV installation in stress test
  powerKw?: number;        // NET power in kW (negative = exporting) for load-based coloring
  houseLoadColoring?: boolean; // Setting: enable load-based coloring (default true)
}

export interface TrafoIconProps {
  isSelected: boolean;
  isHighlighted: boolean;
  loadingPercent?: number;
  isOverloaded?: boolean;
}

export interface KabelverteilschrankIconProps {
  isSelected: boolean;
  isHighlighted: boolean;
}

export interface MuffeIconProps {
  isSelected: boolean;
  isHighlighted: boolean;
  offsetIndex?: number;
}

/**
 * Get icon options (size, anchor) based on element type
 */
export const getIconOptions = (type: string): IconOptions => {
  switch (type) {
    case 'muffe':
      return { iconSize: [12, 12], iconAnchor: [6, 6] };
    case 'hausanschluss':
      return { iconSize: [28, 28], iconAnchor: [14, 14] };
    case 'kabelverteilschrank':
      return { iconSize: [28, 28], iconAnchor: [14, 14] };
    default:
      return { iconSize: [32, 32], iconAnchor: [16, 16] };
  }
};

/**
 * Animation styles for pulsing effects
 */
export const PULSE_STYLE = `
  @keyframes strike-pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.5); opacity: 0.6; }
  }
  .strike-pulse {
    animation: strike-pulse 0.8s ease-in-out infinite;
  }
  @keyframes overload-pulse {
    0%, 100% { box-shadow: 0 0 4px 2px rgba(255, 0, 0, 0.6); }
    50% { box-shadow: 0 0 8px 4px rgba(255, 0, 0, 0.9); }
  }
  .overload-pulse {
    animation: overload-pulse 0.6s ease-in-out infinite;
  }
  @keyframes limited-pulse {
    0%, 100% { box-shadow: 0 0 4px 2px rgba(251, 191, 36, 0.5); }
    50% { box-shadow: 0 0 10px 5px rgba(251, 191, 36, 0.9); }
  }
  .limited-pulse {
    animation: limited-pulse 1.2s ease-in-out infinite;
  }
`;

/**
 * Technical resource type to icon mapping
 */
const RESOURCE_ICONS: Record<string, string> = {
  'pv_anlage': '☀️',
  'battery_storage': '🔋',
  'heat_pump': '🌡️',
  'wallbox': '🔌',
  'air_conditioning': '❄️',
  'other': '⚙️'
};

/**
 * Technical resource type to color mapping (bg + border classes)
 */
const RESOURCE_COLORS: Record<string, { bg: string; border: string }> = {
  'pv_anlage':        { bg: 'bg-sky-400/90 dark:bg-sky-500/80',          border: 'border-sky-600 dark:border-sky-700' },
  'battery_storage':  { bg: 'bg-gray-400/90 dark:bg-gray-400/80',      border: 'border-gray-600 dark:border-gray-600' },
  'heat_pump':        { bg: 'bg-orange-300/90 dark:bg-orange-400/80',   border: 'border-orange-500 dark:border-orange-600' },
  'wallbox':          { bg: 'bg-violet-300/90 dark:bg-violet-400/80',   border: 'border-violet-500 dark:border-violet-600' },
  'air_conditioning': { bg: 'bg-slate-400/90 dark:bg-slate-500/80',    border: 'border-slate-600 dark:border-slate-700' },
};

/**
 * Get inline background style for load-based house coloring.
 * Returns an inline style string or empty string if no stress data.
 * Green (exporting/low) → Yellow → Orange → Red (high draw)
 */
export const getLoadColorStyle = (powerKw?: number, isExporting?: boolean): string => {
  if (powerKw === undefined) return '';
  if (isExporting) return 'background-color: rgba(34,197,94,0.85);';  // green-500
  const absKw = Math.abs(powerKw);
  if (absKw <= 2)  return 'background-color: rgba(74,222,128,0.85);'; // green-400
  if (absKw <= 5)  return 'background-color: rgba(250,204,21,0.85);'; // yellow-400
  if (absKw <= 10) return 'background-color: rgba(251,146,60,0.85);'; // orange-400
  return 'background-color: rgba(239,68,68,0.85);';                   // red-500
};

/**
 * Generate Hausanschluss icon HTML
 */
export const generateHausanschlussIconHtml = (props: HausanschlussIconProps): string => {
  const {
    isSelected,
    isHighlighted,
    hasSMGW,
    hasSteuerbox,
    loadPercentage,
    isOverloaded,
    isLimited,
    technischeRessourcen,
    isExporting,
    hasPv,
    powerKw,
    houseLoadColoring = true
  } = props;

  // Special border for exporting houses (green glow)
  const borderClass = isSelected || isHighlighted
    ? 'border-yellow-400 dark:border-yellow-300'
    : houseLoadColoring && isExporting
      ? 'border-green-500 dark:border-green-400'
      : 'border-gray-800 dark:border-primary-900';

  const pulseClass = isOverloaded ? 'overload-pulse' : isLimited ? 'limited-pulse' : '';

  // Load-based background color (only when enabled and stress data is present)
  const loadBgStyle = houseLoadColoring ? getLoadColorStyle(powerKw, isExporting) : '';

  const parts: string[] = [];

  parts.push(`<style>${PULSE_STYLE}</style>`);

  // Scaling wrapper for zoom-based sizing + expand on click
  const expandClass = isSelected ? 'marker-expanded' : '';
  parts.push(`<div class="marker-expand-wrapper ${expandClass}" style="transform: scale(var(--marker-scale, 1)); transform-origin: center;">`);

  // Main container with overload/limited indicator and load-based coloring
  const bgClass = loadBgStyle ? '' : 'bg-element-connection/80';
  parts.push(`<div class="w-7 h-7 ${bgClass} rounded-full border-2 ${borderClass} ${pulseClass} flex items-center justify-center relative shadow-md" style="${loadBgStyle}">`);

  // House icon — the glyph carries the Anschlussart (⌂ household, G Gewerbe, R RLM, ▮ Säule, ∿ Erzeugung)
  const kind: ConnectionKind = props.connectionKind ?? 'household';
  const glyph = CONNECTION_KIND_GLYPH[kind];
  const glyphSize = glyph === '⌂' ? 'text-[20px] -translate-y-px' : 'text-[14px]';
  parts.push(`<span class="absolute inset-0 flex items-center justify-center ${glyphSize} font-bold text-white" title="${CONNECTION_KIND_LABELS[kind]}">${glyph}</span>`);

  // SMGW indicator (hex position ② top-right)
  if (hasSMGW) {
    parts.push(`<div class="badge-spread-tr absolute w-5 h-5 bg-yellow-400/95 dark:bg-yellow-300/90 rounded-sm border border-orange-500/80 dark:border-orange-400/80 flex items-center justify-center shadow-sm"
      style="top: -4px; left: 19px;">
      <span class="absolute inset-0 flex items-center justify-center text-[12px] font-bold text-orange-700 dark:text-orange-800">⚡</span>
    </div>`);
  }

  // Steuerbox indicator (hex position ③ bottom-right)
  if (hasSteuerbox) {
    parts.push(`<div class="badge-spread-br absolute w-4 h-4 bg-info-500/90 rounded-full border border-gray-800/80 dark:border-primary-900/80 flex items-center justify-center shadow-sm"
      style="top: 14px; left: 20px;">
      <span class="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">⚙</span>
    </div>`);
  }

  // Load percentage indicator (for stress test)
  if (loadPercentage !== undefined && loadPercentage > 0) {
    const bgColor = isOverloaded
      ? 'bg-error-600/90 dark:bg-error-500/90 border-error-800/90'
      : 'bg-warning-600/70 dark:bg-warning-500/80 border-warning-800/70 dark:border-warning-700/80';
    parts.push(`<div class="badge-spread-tl absolute -top-3 -left-3 w-6 h-6 ${bgColor} rounded-full border-2 flex items-center justify-center shadow-sm">
      <span class="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white">${fmt(loadPercentage, 0)}%</span>
    </div>`);
  }

  // Technical resources indicators - fixed positions at the edge of the house icon
  if (technischeRessourcen?.length) {
    const positions = [
      { style: 'top: -10px; left: 6px;', spread: 'badge-spread-t' },
      { style: 'top: 22px; left: 6px;', spread: 'badge-spread-b' },
      { style: 'top: 14px; left: -8px;', spread: 'badge-spread-bl' },
      { style: 'top: -2px; left: -8px;', spread: 'badge-spread-tl' },
    ];

    technischeRessourcen.forEach((tr, index) => {
      const p = positions[index % positions.length];
      const colors = RESOURCE_COLORS[tr.type] || { bg: 'bg-purple-500/70 dark:bg-purple-400/80', border: 'border-gray-800/70 dark:border-primary-900/70' };

      parts.push(`<div class="${p.spread} absolute w-4 h-4 ${colors.bg} rounded-full border ${colors.border} flex items-center justify-center shadow-sm"
        style="${p.style}">
        <span class="absolute inset-0 flex items-center justify-center text-[10px]">${RESOURCE_ICONS[tr.type] || '?'}</span>
      </div>`);
    });
  }

  // Power limit indicator (control box active - pulsing orange badge)
  if (isLimited) {
    parts.push(`<div class="badge-spread-bl absolute -bottom-2 -left-2 w-5 h-5 bg-amber-500 dark:bg-amber-400 rounded-full border-2 border-amber-700 dark:border-amber-600 flex items-center justify-center strike-pulse shadow-md">
      <span class="absolute inset-0 flex items-center justify-center text-[12px] font-bold text-white dark:text-amber-900">⚠</span>
    </div>`);
  }

  // PV export indicator (for stress test - shows sun icon when exporting)
  if (hasPv && isExporting) {
    parts.push(`<div class="badge-spread-tr absolute -top-2.5 -right-2.5 w-5 h-5 bg-green-500/90 dark:bg-green-400/90 rounded-full border border-green-700/80 dark:border-green-600/80 flex items-center justify-center shadow-sm">
      <span class="absolute inset-0 flex items-center justify-center text-[12px]">☀️</span>
    </div>`);
  } else if (hasPv && !isExporting) {
    // PV but not exporting (consuming more than producing)
    parts.push(`<div class="badge-spread-tr absolute -top-2.5 -right-2.5 w-5 h-5 bg-yellow-500/90 dark:bg-yellow-400/90 rounded-full border border-yellow-700/80 dark:border-yellow-600/80 flex items-center justify-center shadow-sm">
      <span class="absolute inset-0 flex items-center justify-center text-[12px]">☀️</span>
    </div>`);
  }

  // Close main container + scaling wrapper
  parts.push(`</div>`);
  parts.push(`</div>`);

  return parts.join('');
};

/**
 * Generate Trafostation icon HTML
 */
export const generateTrafoIconHtml = (props: TrafoIconProps): string => {
  const { isSelected, isHighlighted, loadingPercent, isOverloaded } = props;

  const borderClass = isSelected || isHighlighted
    ? 'border-yellow-400 dark:border-yellow-300'
    : 'border-gray-800 dark:border-primary-900';

  const overloadClass = isOverloaded ? 'overload-pulse' : '';

  let html = `<style>${PULSE_STYLE}</style>`;
  html += `<div class="w-8 h-8 bg-element-trafo/60 dark:bg-element-trafo/70 rounded-lg border-2 ${borderClass} ${overloadClass} flex items-center justify-center relative shadow-md">`;
  html += `<span class="absolute inset-0 flex items-center justify-center text-[18px] font-bold text-gray-800 dark:text-amber-100 translate-y-[-2px]">⚡</span>`;

  // Loading percentage badge for stress test
  if (loadingPercent !== undefined && loadingPercent > 0) {
    const bgColor = isOverloaded
      ? 'bg-error-600/90 border-error-800'
      : loadingPercent > LOADING_WARNING_PCT
        ? 'bg-warning-600/90 border-warning-800'
        : 'bg-info-600/90 border-info-800';
    html += `<div class="absolute -top-2 -right-2 w-6 h-6 ${bgColor} rounded-full border flex items-center justify-center shadow-sm">
      <span class="text-[8px] font-bold text-white">${fmt(loadingPercent, 0)}%</span>
    </div>`;
  }

  html += `</div>`;
  return html;
};

/**
 * Generate Kabelverteilschrank icon HTML
 */
export const generateKabelverteilschrankIconHtml = (props: KabelverteilschrankIconProps): string => {
  const { isSelected, isHighlighted } = props;

  const borderClass = isSelected || isHighlighted
    ? 'border-yellow-400 dark:border-yellow-300'
    : 'border-gray-800 dark:border-primary-900';

  return `<div class="w-7 h-7 bg-element-cabinet/70 dark:bg-element-cabinet/80 rounded-sm border-2 ${borderClass} flex items-center justify-center relative shadow-md">
    <span class="absolute inset-0 flex items-center justify-center text-[18px] font-bold text-white dark:text-blue-50 -translate-y-px">□</span>
  </div>`;
};

/**
 * Generate Muffe icon HTML
 */
export const generateMuffeIconHtml = (props: MuffeIconProps): string => {
  const { isSelected, isHighlighted, offsetIndex } = props;

  const borderClass = isSelected || isHighlighted
    ? 'border-yellow-400 dark:border-yellow-300'
    : 'border-gray-800 dark:border-primary-900';

  let html = `<div class="w-4 h-4 bg-element-splice/60 dark:bg-element-splice/70 rounded-full border-2 ${borderClass} relative shadow-sm">`;

  if (offsetIndex !== undefined) {
    html += `<span class="absolute -top-2 -right-2 text-[10px] bg-white dark:bg-primary-700 rounded-full w-4 h-4 flex items-center justify-center border border-gray-800 dark:border-primary-900 text-gray-800 dark:text-primary-100 shadow-sm">${offsetIndex + 1}</span>`;
  }

  html += `</div>`;
  return html;
};
